import { WarningOutlined, WatchLater } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IconButton } from '@mui/material';
import { useSelector } from 'react-redux';

import { ItemAvailabilityStatus } from 'entities/CatalogItem/lib/itemAvailability';
import { Proposal } from 'entities/Proposal/model/types/ProposalSchema';
import { getUserIsMaintenanceTechnician } from 'entities/User/model/selectors/userSelectors';
import { VendorActivationStatus } from 'entities/Vendors';
import { UnavailableTooltipContent } from 'shared/ui/ItemAvailabilityModal';
import { useProposalsStatus } from 'shared/ui/Suppliers/lib/hooks/useProposalsStatus';
import { DynamicTooltip } from 'shared/ui/Tooltip/ui/DynamicTooltip';

interface PropertyCatalogItemSupplierStatusIconProps {
    availabilityStatus: ItemAvailabilityStatus;
    proposals: Proposal[];
    onLearnMore?: () => void;
}

const getStatusIcon = (aggregateStatus: VendorActivationStatus | null) => {
    if (aggregateStatus === VendorActivationStatus.INVALID) {
        return (
            <WarningOutlined
                sx={{
                    fontSize: '20px',
                    color: 'error.light',
                    cursor: 'pointer',
                }}
            />
        );
    }

    if (aggregateStatus === VendorActivationStatus.VERIFYING) {
        return (
            <WatchLater
                sx={{
                    fontSize: '20px',
                    color: 'warning.700',
                    cursor: 'pointer',
                }}
            />
        );
    }

    return (
        <InfoOutlinedIcon
            sx={{
                fontSize: '20px',
                color: 'grey.400',
                cursor: 'pointer',
            }}
        />
    );
};

export const PropertyCatalogItemSupplierStatusIcon = ({
    availabilityStatus,
    proposals,
    onLearnMore,
}: PropertyCatalogItemSupplierStatusIconProps) => {
    const aggregateStatus = useProposalsStatus(proposals);
    const isMaintenanceTechnician = useSelector(getUserIsMaintenanceTechnician);

    return (
        <DynamicTooltip
            maxWidth={400}
            interactive
            containerStyle={{ display: 'inline-flex' }}
            content={(closeTooltip) => (
                <UnavailableTooltipContent
                    availabilityStatus={availabilityStatus}
                    hideReplacementLink={isMaintenanceTechnician}
                    onLearnMore={
                        onLearnMore
                            ? () => {
                                  closeTooltip();
                                  onLearnMore();
                              }
                            : undefined
                    }
                />
            )}
        >
            <IconButton
                data-testclass="item-suppliers-availability-info-icon"
                aria-label="Supplier status"
                size="small"
                sx={{ p: 0 }}
            >
                {getStatusIcon(aggregateStatus)}
            </IconButton>
        </DynamicTooltip>
    );
};
